import { TenantTsType } from '@skygroup/shared/constants/tenantTs';
import { getTsTypeByIdentifier } from '@skygroup/shared/utils/flightIdentifier';
import { BundleFlightData } from '../../modules/FlightDetails/flightSlice';

/**
 * Suppliers with ts_type === SKY (local inventory) should appear first in the
 * combined results list. Flights arrive in chunks (one ts_type per SSE
 * message), so priority chunks are prepended to the top of the list while all
 * other chunks are appended to the bottom.
 */
export const isPriorityTsType = (ts_type?: string | number): boolean =>
  ts_type === TenantTsType.SKY;

export const isSkyFlight = (flight: { identifier?: string }): boolean =>
  getTsTypeByIdentifier({ identifier: flight.identifier, type: 'flight' }) ===
  TenantTsType.SKY;

export const getFlightPrice = (flight: {
  price?: { total?: number; grandTotal?: number };
}): number => flight.price?.total ?? flight.price?.grandTotal ?? 0;

/**
 * Returns how many flights to add to the tracked SKY-priority prefix length.
 */
export const getPriorityFlightCountDelta = (
  newFlightsCount: number,
  ts_type?: string | number
): number => (isPriorityTsType(ts_type) ? newFlightsCount : 0);

/**
 * Merges a newly received chunk of flights into the combined list, placing
 * priority (SKY) flights at the top and all others at the bottom.
 * Mutates `existingFlights` in place (safe to use inside Redux Toolkit/immer
 * reducers).
 */
export const mergeFlightsByTsTypePriority = (
  existingFlights: BundleFlightData[],
  newFlights: BundleFlightData[],
  ts_type?: string | number
): void => {
  if (isPriorityTsType(ts_type)) {
    existingFlights.unshift(...newFlights);
  } else {
    existingFlights.push(...newFlights);
  }
};

/**
 * Sorts flights by price within the SKY prefix and the remainder separately.
 * When there are no SKY flights, sorts the full array.
 */
export const sortFlightsByPriorityAndPrice = (
  flights: BundleFlightData[],
  priorityFlightCount: number
): void => {
  if (flights.length <= 1) {
    return;
  }

  const compareByPriceAsc = (a: BundleFlightData, b: BundleFlightData) =>
    getFlightPrice(a) - getFlightPrice(b);

  const priorityEnd = Math.min(Math.max(0, priorityFlightCount), flights.length);

  if (priorityEnd === 0 || priorityEnd === flights.length) {
    flights.sort(compareByPriceAsc);
    return;
  }

  const prefix = flights.slice(0, priorityEnd);
  const suffix = flights.slice(priorityEnd);
  prefix.sort(compareByPriceAsc);
  suffix.sort(compareByPriceAsc);
  flights.splice(0, flights.length, ...prefix, ...suffix);
};
